import React, { Component } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { injectIntl, defineMessages } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { withGrowl } from '../../../../components/GrowlNotifications';

import { getUserLanguage } from '../../../../utils/LanguageUtil';
import { sortUrl, toSortOrder, parse } from '../../../../utils/UrlUtil';
import AssessmentListTableHeader from './SuperlinkAssessmentListHeader';
import Paginator from '../../../../components/Paginator';
import { intlShape, growlShape } from '../../../../components/prop-types';

import { removeAssessmentFromSuperLink } from '../../../../services/apps/SuperLinkService';

const messages = defineMessages({
  new: { id: 'candidate.status.NEW', defaultMessage: 'NEW' },
  invited: { id: 'candidate.status.INVITED', defaultMessage: 'INVITED' },
  started: { id: 'candidate.status.STARTED', defaultMessage: 'IN PROGRESS' },
  declined: { id: 'candidate.status.DECLINED', defaultMessage: 'DECLINED' },
  expired: { id: 'candidate.status.EXPIRED', defaultMessage: 'EXPIRED' },
  submitted: { id: 'candidate.status.SUBMITTED', defaultMessage: 'ASSESSED' },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  template: { id: 'attrs.assessmentTemplate', defaultMessage: 'Assessment Template' },
  language: { id: 'attrs.language', defaultMessage: 'Language' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
  action: { id: 'attrs.actions', defaultMessage: 'Actions' },
  success: { id: 'noti.success', defaultMessage: 'Success!' },
});

const getItemStatusProperties = (status, formatMessage) => {
  switch (status) {
    case 'NEW': {
      return {
        styleClass: 'filter is-active',
        text: formatMessage(messages.new),
      };
    }
    case 'INVITED': {
      return {
        styleClass: 'filter filter--info is-active',
        text: formatMessage(messages.invited),
      };
    }
    case 'STARTED': {
      return {
        styleClass: 'filter filter--warning is-active',
        text: formatMessage(messages.started),
      };
    }
    case 'DECLINED': {
      return {
        styleClass: 'filter filter--error is-active',
        text: formatMessage(messages.declined),
      };
    }
    case 'EXPIRED': {
      return {
        styleClass: 'filter filter--error is-active',
        text: formatMessage(messages.expired),
      };
    }
    case 'SUBMITTED': {
      return {
        styleClass: 'filter filter--success is-active',
        text: formatMessage(messages.submitted),
      };
    }
    default: {
      return {};
    }
  }
};

const statusTemplate = (rowData, { formatMessage }) => {
  const statusProp = getItemStatusProperties(rowData.status, formatMessage);
  const { styleClass, text } = statusProp;
  if (statusProp) {
    return <span className={styleClass}>{text}</span>;
  }

  return null;
};

const nameTemplate = (rowData, { currentAccount }) => {
  const detailUrl = `/accounts/${currentAccount}/assessments/${rowData.id}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData.title}
    </Link>
  );
};

const languageTemplate = (rowData, col) => {
  const language = getUserLanguage(rowData.locale, col.translatedTags);
  return language.label;
};

const createdTemplate = (rowData, { formatDate }) => {
  const updated = formatDate(moment(rowData.createdOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};


class AssessmentList extends Component {
  constructor(props) {
    super(props);
    this.onSortChange = this.onSortChange.bind(this);
    this.handleRemoveAssessment = this.handleRemoveAssessment.bind(this);
    this.actionsTemplate = this.actionsTemplate.bind(this);
  }

  onSortChange(event) {
    const { location, history } = this.props;
    sortUrl(event, location, history);
  }

  actionsTemplate(rowData, props) {
    const { superLinkId } = props;

    const disabled = rowData.status === 'SUBMITTED';

    return (
      <Button
        icon="pi pi-times"
        className="p-button-secondary"
        disabled={disabled}
        onClick={() => this.handleRemoveAssessment(rowData.id, superLinkId, props)}
      />
    );
  }

  async handleRemoveAssessment(id, superLinkId, props) {
    const {
      currentAccount,
      growl,
      formatMessage,
    } = props;

    try {
      await removeAssessmentFromSuperLink({
        accountId: currentAccount,
        superLinkId,
        assessmentId: id,
      });
      growl.show({ severity: 'info', detail: formatMessage(messages.success) });
    } catch (err) {
      growl.show({ severity: 'error', sticky: true, detail: err.message });
    }
  }

  render() {
    const {
      connection: {
        edges, totalCount, indexFrom, indexTo, pageInfo,
      },
      intl: { formatMessage, formatDate },
      growl,
      currentAccount,
      superLinkId,
      translatedTags,
      loading,
      location,
    } = this.props;

    const params = parse(location.search);
    const { sort = {} } = params;
    const { sortBy, sortDir } = sort;

    // data list
    const listRows = edges.map(({ node }) => ({
      ...node,
      title: node.respondent ? `${node.respondent.firstName} ${node.respondent.lastName}` : '',
      template: node.template ? node.template.title : '',
    }));

    // header
    const nameHeader = formatMessage(messages.name);
    const templateHeader = formatMessage(messages.template);
    const languageHeader = formatMessage(messages.language);
    const statusHeader = formatMessage(messages.status);
    const createdHeader = formatMessage(messages.created);
    const actionHeader = formatMessage(messages.action);

    // footer
    const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

    return (
      <Panel>
        <div className="p-grid">
          <div className="p-col-12">
            <AssessmentListTableHeader formatMessage={formatMessage} />
          </div>
          <div className="p-col-12">
            <DataTable
              value={listRows}
              responsive
              sortField={sortBy}
              sortOrder={toSortOrder(sortDir)}
              onSort={this.onSortChange}
              lazy
              loading={loading}
              autoLayout
            >
              <Column
                field="title"
                header={nameHeader}
                currentAccount={currentAccount}
                body={nameTemplate}
                sortable
              />
              <Column
                field="template"
                header={templateHeader}
              />
              <Column
                field="locale"
                header={languageHeader}
                body={languageTemplate}
                translatedTags={translatedTags}
              />
              <Column
                field="status"
                header={statusHeader}
                body={statusTemplate}
                formatMessage={formatMessage}
              />
              <Column
                field="createdOn"
                header={createdHeader}
                sortable
                formatDate={formatDate}
                body={createdTemplate}
              />
              <Column
                header={actionHeader}
                body={this.actionsTemplate}
                currentAccount={currentAccount}
                superLinkId={superLinkId}
                growl={growl}
                formatMessage={formatMessage}
                style={{ width: '3.5em' }}
              />
            </DataTable>
          </div>
          <div className="p-col-12 align-center">{footer}</div>
        </div>
      </Panel>
    );
  }
}

AssessmentList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: intlShape.isRequired,
  growl: growlShape.isRequired,
  currentAccount: PropTypes.string.isRequired,
  superLinkId: PropTypes.string.isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
  translatedTags: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default withRouter(withGrowl(injectIntl(AssessmentList)));
